import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, Share2, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const FinancialReports = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isHQAdmin = user?.role === 'HQ_ADMIN';
  
  const [reports, setReports] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  const [formData, setFormData] = useState({
    report_name: '',
    report_type: 'Fee Summary',
    date_range_start: '',
    date_range_end: '',
    franchisee_ids: [],
    selected_fields: ['franchisee_id', 'period', 'monthly_royalty', 'franchise_fee', 'total', 'payment_status'],
    notes: ''
  });

  const [shareUserIds, setShareUserIds] = useState([]);

  const availableFields = [
    { value: 'franchisee_id', label: 'Franchisee ID' },
    { value: 'period', label: 'Period' },
    { value: 'monthly_royalty', label: 'Monthly Royalty' },
    { value: 'franchise_fee', label: 'Franchise Fee' },
    { value: 'total', label: 'Total Amount' },
    { value: 'payment_status', label: 'Payment Status' },
    { value: 'submitted_at', label: 'Submitted Date' },
    { value: 'submitted_by', label: 'Submitted By' },
    { value: 'approved_by', label: 'Approved By' },
    { value: 'notes', label: 'Notes' }
  ];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [reportsRes, franchiseesRes, usersRes] = await Promise.all([
        axios.get(`${API}/financial/reports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/franchisees`, { headers: { Authorization: `Bearer ${token}` } }),
        isHQAdmin ? axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: [] })
      ]);
      setReports(reportsRes.data);
      setFranchisees(franchiseesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (fieldValue) => {
    setFormData(prev => ({
      ...prev,
      selected_fields: prev.selected_fields.includes(fieldValue)
        ? prev.selected_fields.filter(f => f !== fieldValue)
        : [...prev.selected_fields, fieldValue]
    }));
  };

  const handleFranchiseeToggle = (franchiseeId) => {
    setFormData(prev => ({
      ...prev,
      franchisee_ids: prev.franchisee_ids.includes(franchiseeId)
        ? prev.franchisee_ids.filter(id => id !== franchiseeId)
        : [...prev.franchisee_ids, franchiseeId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/financial/reports`, {
        ...formData,
        created_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report generated successfully');
      setOpenDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewDialog(true);
  };

  const handleShareReport = async () => {
    if (!selectedReport || shareUserIds.length === 0) return;
    
    try {
      await axios.post(`${API}/financial/reports/${selectedReport.report_id}/share`, {
        user_ids: shareUserIds,
        shared_by: user.full_name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report shared successfully');
      setShareDialog(false);
      setShareUserIds([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to share report');
    }
  };

  const exportToPDF = (report) => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text(report.report_name, 14, 20);
    
    doc.setFontSize(11);
    doc.text(`Type: ${report.report_type}`, 14, 30);
    doc.text(`Period: ${report.date_range_start} to ${report.date_range_end}`, 14, 37);
    doc.text(`Created by: ${report.created_by}`, 14, 44);
    doc.text(`Created at: ${new Date(report.created_at).toLocaleString()}`, 14, 51);
    
    const tableData = report.data.map(row => 
      report.selected_fields.map(field => row[field] || '-')
    );
    
    doc.autoTable({
      startY: 60,
      head: [report.selected_fields.map(f => {
        const field = availableFields.find(af => af.value === f);
        return field ? field.label : f;
      })],
      body: tableData,
    });
    
    doc.save(`${report.report_name}.pdf`);
    toast.success('Report exported to PDF');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="financial-reports-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Financial Reports</h1>
            <p className="text-muted-foreground mt-1">Generate and manage custom financial reports</p>
          </div>
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <Button data-testid="generate-report-btn">
                <Plus className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Generate Financial Report</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Report Name*</Label>
                  <Input value={formData.report_name} onChange={(e) => setFormData({...formData, report_name: e.target.value})} required data-testid="report-name-input" />
                </div>
                
                <div className="space-y-2">
                  <Label>Report Type*</Label>
                  <Select value={formData.report_type} onValueChange={(value) => setFormData({...formData, report_type: value})}>
                    <SelectTrigger data-testid="report-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fee Summary">Fee Summary</SelectItem>
                      <SelectItem value="Payment History">Payment History</SelectItem>
                      <SelectItem value="Franchisee Financial">Franchisee Financial</SelectItem>
                      <SelectItem value="Custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date*</Label>
                    <Input type="date" value={formData.date_range_start} onChange={(e) => setFormData({...formData, date_range_start: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date*</Label>
                    <Input type="date" value={formData.date_range_end} onChange={(e) => setFormData({...formData, date_range_end: e.target.value})} required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Franchisees (Leave empty for all)</Label>
                  <div className="border rounded-lg p-4 max-h-40 overflow-y-auto space-y-2">
                    {franchisees.map(franchisee => (
                      <div key={franchisee.franchisee_id} className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.franchisee_ids.includes(franchisee.franchisee_id)}
                          onCheckedChange={() => handleFranchiseeToggle(franchisee.franchisee_id)}
                        />
                        <label className="text-sm">{franchisee.company_name} ({franchisee.franchise_id})</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Fields to Include*</Label>
                  <div className="border rounded-lg p-4 max-h-48 overflow-y-auto grid grid-cols-2 gap-2">
                    {availableFields.map(field => (
                      <div key={field.value} className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.selected_fields.includes(field.value)}
                          onCheckedChange={() => handleFieldToggle(field.value)}
                        />
                        <label className="text-sm">{field.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-report-btn">Generate Report</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Generated Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-muted-foreground">Loading...</p>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No reports generated yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.report_id} data-testid={`report-row-${report.report_name}`}>
                      <TableCell className="font-medium">{report.report_name}</TableCell>
                      <TableCell><Badge>{report.report_type}</Badge></TableCell>
                      <TableCell className="text-sm">{report.date_range_start} to {report.date_range_end}</TableCell>
                      <TableCell>{report.created_by}</TableCell>
                      <TableCell>{new Date(report.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="tabular-nums">{report.data.length}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleViewReport(report)} data-testid={`view-report-${report.report_name}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => exportToPDF(report)} data-testid={`export-report-${report.report_name}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          {report.created_by === user.email && isHQAdmin && (
                            <Button size="sm" variant="outline" onClick={() => { setSelectedReport(report); setShareDialog(true); }}>
                              <Share2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* View Report Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedReport?.report_name}</DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium">{selectedReport.report_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Period</p>
                    <p className="font-medium">{selectedReport.date_range_start} to {selectedReport.date_range_end}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Created By</p>
                    <p className="font-medium">{selectedReport.created_by}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Records</p>
                    <p className="font-medium tabular-nums">{selectedReport.data.length}</p>
                  </div>
                </div>

                <div className="border rounded-lg overflow-auto max-h-96">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {selectedReport.selected_fields.map(field => {
                          const fieldConfig = availableFields.find(f => f.value === field);
                          return <TableHead key={field}>{fieldConfig?.label || field}</TableHead>;
                        })}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReport.data.map((row, idx) => (
                        <TableRow key={idx}>
                          {selectedReport.selected_fields.map(field => (
                            <TableCell key={field}>{row[field] || '-'}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Share Report Dialog */}
        <Dialog open={shareDialog} onOpenChange={setShareDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Select users to share this report with:</p>
              <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                {users.filter(u => u.user_id !== user.user_id).map(u => (
                  <div key={u.user_id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={shareUserIds.includes(u.user_id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setShareUserIds([...shareUserIds, u.user_id]);
                        } else {
                          setShareUserIds(shareUserIds.filter(id => id !== u.user_id));
                        }
                      }}
                    />
                    <label className="text-sm">{u.full_name} ({u.email})</label>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShareDialog(false)}>Cancel</Button>
                <Button onClick={handleShareReport} disabled={shareUserIds.length === 0}>Share Report</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FinancialReports;
